import * as PropTypes from 'prop-types';
import React, { useRef, useEffect } from 'react';
import Textarea from 'react-textarea-autosize';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { isValidDateOfBirth } from '@medflex/shared-utils';
import isDesktop from '@medflex/utils/isDesktop';
import { IdentificationIcon } from '@heroicons/react/24/outline';
import InputField from '../InputField';
import FormErrorMessage from '../FormErrorMessage';
import Button from '../Button';
import DateOfBirthField from '../DateOfBirth/DateOfBirthField';

const NOTE_MAX_LENGTH = 500;

const ContactInfoEdit = ({ onSubmit, state, initialValues, disabled, autofocus }) => {
  const { t } = useTranslation('components', { keyPrefix: 'ContactInfoEdit' });
  const validationSchema = yup.object().shape({
    firstName: yup.string().trim().required(t('validation.required')),
    lastName: yup.string().trim().required(t('validation.required')),
    dateOfBirth: yup
      .string()
      .test('is proper date of birth', t('validation.validAge'), value =>
        isValidDateOfBirth(new Date(value)),
      )
      .required(t('validation.required')),
  });

  const {
    register: registerFormElement,
    errors,
    control,
    handleSubmit: wrapSubmit,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });
  const firstNameRef = useRef();
  const noteRef = useRef();

  useEffect(() => {
    if (firstNameRef.current) {
      registerFormElement(firstNameRef.current);
    }
  }, [autofocus, registerFormElement]);

  return (
    <form data-component="ContactInfoEdit" onSubmit={wrapSubmit(onSubmit)}>
      <div className="mb-6">
        <label className="block mb-1 font-semibold" htmlFor="contactNote">
          {t('first_name.label')}
        </label>
        <InputField
          name="firstName"
          id="firstName"
          state={errors.firstName ? 'invalid' : 'default'}
          defaultValue={initialValues && initialValues.firstName}
          ref={firstNameRef}
          readOnly={state === 'loading'}
          placeholder={t('first_name.placeholder')}
        />
        {errors.firstName && <FormErrorMessage message={errors.firstName.message} />}
      </div>
      <div className="mb-6">
        <label className="block mb-1 font-semibold" htmlFor="lastName">
          {t('last_name.label')}
        </label>
        <InputField
          name="lastName"
          id="lastName"
          ref={registerFormElement}
          state={errors.lastName ? 'invalid' : 'default'}
          defaultValue={initialValues && initialValues.lastName}
          readOnly={state === 'loading'}
          placeholder={t('last_name.placeholder')}
        />
        {errors.lastName && <FormErrorMessage message={errors.lastName.message} />}
      </div>
      <div className="mb-6">
        <label className="block mb-1 font-semibold leading-5 w-full" htmlFor="dateOfBirth">
          {t('date_of_birth.label')}
        </label>
        <div className="flex flex-row space-x-2 mb-2">
          <DateOfBirthField
            name="dateOfBirth"
            control={control}
            defaultValue={initialValues && initialValues.dateOfBirth}
            state={errors.dateOfBirth ? 'invalid' : 'default'}
          />
        </div>
        {errors.dateOfBirth && <FormErrorMessage message={errors.dateOfBirth.message} />}
      </div>

      {isDesktop() && (
        <div className="mb-4">
          <label className="flex mb-1 font-semibold" htmlFor="contactPvsId">
            <IdentificationIcon className="w-6 h-6" />
            <span className="ml-2">{t('pvsId.label')}</span>
          </label>
          <InputField
            type="text"
            name="pvsId"
            id="contactPvsId"
            defaultValue={initialValues.pvsId || ''}
            ref={registerFormElement}
          />
          <div className="text-xs text-slate-600">{t('pvsId.info')}</div>
        </div>
      )}

      <div className="mb-8">
        <label className="block mb-1 font-semibold" htmlFor="contactNote">
          {t('note.label')}
        </label>
        <Controller
          control={control}
          name="note"
          defaultValue={initialValues.note || ''}
          render={({ onChange, value, name }) => (
            <Textarea
              className="h-32 w-full rounded border border-slate-400 p-3 placeholder-slate-500 outline-none focus:border-blue-400"
              id="contactNote"
              minRows={4}
              name={name}
              onChange={onChange}
              maxLength={NOTE_MAX_LENGTH}
              value={value}
              ref={noteRef}
            />
          )}
        />
        <div className="text-xs text-slate-600">{t('note.info')}</div>
      </div>

      <div>
        <Button
          className="flex-none w-full"
          isDisabled={disabled}
          isLoading={state === 'loading'}
          type="submit"
        >
          {t('button')}
        </Button>
      </div>
    </form>
  );
};

ContactInfoEdit.defaultProps = {
  state: 'composing',
  disabled: false,
  autofocus: true,
  className: null,
};

ContactInfoEdit.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  state: PropTypes.oneOf(['composing', 'loading']),
  disabled: PropTypes.bool,
  autofocus: PropTypes.bool,
  initialValues: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    dateOfBirth: PropTypes.string,
    note: PropTypes.string,
    pvsId: PropTypes.string,
  }).isRequired,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
};

export default ContactInfoEdit;
